import { ROUTES } from '../../../../constants';
import { Attachment, AttachmentType } from '../../../../types';
import { getVideoPreviewSrc } from '../../../util/isWebUrl';
import { logger } from '../../../util/logger';
import { httpClient } from '../../httpClient';
import { BaseMediaUploader } from './baseMediaUploader';
import { resolveUploadMimeType, SUPPORTED_ATTACHMENT_FILES } from './constants';
import { MediaProbeResult } from './probeMediaDimensions';

type DynamicsUploadUrlResponse = {
  uploadUrl: string;
  filePath: string;
  expiresInSeconds?: string;
  attachmentUrl: string;
};

export class DynamicsMediaUploader extends BaseMediaUploader<DynamicsUploadUrlResponse> {
  protected readonly requiredResponseFields = ['uploadUrl', 'filePath', 'attachmentUrl'] as const;
  static readonly uploaderName = 'DynamicsMediaUploader';
  // Legacy/Dynamics media platform accepts the full image + video set.
  static readonly supportedFileExtensions = SUPPORTED_ATTACHMENT_FILES;

  protected async getUploadUrl(): Promise<DynamicsUploadUrlResponse> {
    try {
      const { data } = await httpClient
        .getInstance()
        .post<DynamicsUploadUrlResponse>(ROUTES.MEDIA_UPLOAD_URL_API, {
          fileName: this.file.name,
          mimeType: resolveUploadMimeType(this.file),
        });
      return data;
    } catch (error) {
      logger.error({ message: 'failed to get dynamics upload url', error });
      throw error;
    }
  }

  protected buildAttachment(response: DynamicsUploadUrlResponse, probe: MediaProbeResult): Attachment {
    const { attachmentUrl, filePath } = response;
    const mimeType = resolveUploadMimeType(this.file);
    const common = {
      name: this.file.name,
      size: this.file.size,
      width: probe.width,
      height: probe.height,
      url: attachmentUrl,
      attachingMethod: this.attachingMethod,
      mimeType,
      filePath,
    };

    if (mimeType.startsWith('image/')) {
      return { ...common, type: AttachmentType.Image };
    }

    return {
      ...common,
      type: AttachmentType.Video,
      iconUrl: getVideoPreviewSrc({ iconUrl: probe.thumbnailUrl, url: common.url }),
      duration: Math.round((probe.duration ?? 0) * 1000),
      format: probe.format ?? '',
    };
  }
}
